import React, { FC } from 'react';
import { Dispatch } from 'redux';
import styled from 'styled-components';
import { Dropdown, Menu as MenuAntD, Tooltip } from 'antd';
import { useDispatch, useSelector } from 'react-redux';
import { SettingOutlined } from 'src/theme/icons';
import {
  AssessmentStatus,
  CompanyState,
  RootState,
  TypeOfTraining,
} from 'src/store/types';
import { archiveCompanyRequest } from 'src/store/actions/companyListing';
import {
  getCompanyById,
  getIndexedAssessmentsByCompanyId,
} from 'src/store/selectors';

export interface CompanyListingTableSettingsProps {
  companyId: number;
  showSetupAssessmentPanel: (companyId: number) => void;
  showEditCompanyPanel: (company: CompanyState) => void;
  showNewUserPanel: (companyId: number) => void;
}

const DropdownIcon = styled.div`
  align-items: center;
  display: flex;
  height: 21px;
  justify-content: flex-end;
  width: 80px;

  > span {
    color: ${({ theme }) => theme.colors.lightBlue.main};
    cursor: pointer;
    font-size: 20px;
  }
`;

const Menu = styled(MenuAntD)`
  border-radius: 4px;
  margin-top: -35px;
  margin-left: 12px;
  width: 180px;

  .ant-dropdown-menu-item:hover {
    &.title {
      background-color: white;
      cursor: default;
    }
  }

  .ant-dropdown-menu-item-disabled {
    span {
      color: ${({ theme }) => theme.colors.grayscale.middleGray};
    }
  }

  .title {
    align-items: center;
    display: flex;
    justify-content: space-between;
    border-bottom: ${({ theme }) => `1px solid ${theme.colors.lightBlue.main}`};

    > span {
      font-weight: 600;
    }

    .title-icon {
      color: ${({ theme }) => theme.colors.lightBlue.main};
      font-size: 20px;
    }
  }

  .title-divider {
    color: red;
  }

  .ant-dropdown-menu-item-divider {
    margin: 0px 4px;
    width: 172px;
  }

  span {
    color: ${({ theme }) => theme.colors.darkBlue.main};
    font-family: ${({ theme }) => theme.fontFamilies.primary};
    font-size: 14px;
  }
`;

const menu = ({
  company,
  dispatch,
  showSetupAssessmentPanel,
  showEditCompanyPanel,
  showNewUserPanel,
  canCreateNewAssessment,
}: {
  company: CompanyState;
  dispatch: Dispatch;
  showSetupAssessmentPanel: (companyId: number) => void;
  showEditCompanyPanel: (company: CompanyState) => void;
  showNewUserPanel: (companyId: number) => void;
  canCreateNewAssessment: boolean;
}) => (
  <Menu>
    <Menu.Item
      key="0"
      onClick={() => showSetupAssessmentPanel(company.id)}
      disabled={!canCreateNewAssessment}
    >
      <Tooltip
        title={
          !canCreateNewAssessment
            ? 'You can only have one active assessment at a time'
            : ''
        }
      >
        <span>Setup new assessment</span>
      </Tooltip>
    </Menu.Item>
    <Menu.Item key="1" onClick={() => showEditCompanyPanel(company)}>
      <span>Edit company profile</span>
    </Menu.Item>
    <Menu.Item
      key="2"
      onClick={() => dispatch(archiveCompanyRequest(company.id))}
    >
      <span>Archive company</span>
    </Menu.Item>
    <Menu.Item key="3" onClick={() => showNewUserPanel(company.id)}>
      <span>Create user</span>
    </Menu.Item>
  </Menu>
);

export const CompanyListingTableSettings: FC<
  CompanyListingTableSettingsProps
> = ({
  companyId,
  showSetupAssessmentPanel,
  showEditCompanyPanel,
  showNewUserPanel,
}) => {
  const dispatch = useDispatch();
  const company = useSelector((state: RootState) =>
    getCompanyById(state, companyId),
  );
  const companyAssessments = useSelector((state: RootState) =>
    getIndexedAssessmentsByCompanyId(state, companyId),
  );

  const canCreateNewAssessment = companyAssessments
    ? Object.values(companyAssessments).every(
        (assessment) =>
          assessment && assessment.status === AssessmentStatus.COMPLETED,
      ) && company.typeOfTraining !== TypeOfTraining.TRAINING_ONLY
    : false;

  return (
    <Dropdown
      overlay={menu({
        company,
        dispatch,
        showSetupAssessmentPanel,
        showEditCompanyPanel,
        showNewUserPanel,
        canCreateNewAssessment,
      })}
      trigger={['click']}
    >
      <DropdownIcon>
        <SettingOutlined />
      </DropdownIcon>
    </Dropdown>
  );
};
